import React, { useState, useEffect } from 'react';
import { 
  CogIcon, 
  SparklesIcon, 
  ClockIcon,
  CheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { MCPProcessingProfile } from '../types/DocumentTypes';

interface MCPProcessingProfileSelectorProps {
  selectedProfile: string;
  onProfileChange: (profileId: string) => void;
  disabled?: boolean;
}

export const MCPProcessingProfileSelector: React.FC<MCPProcessingProfileSelectorProps> = ({
  selectedProfile,
  onProfileChange,
  disabled = false
}) => {
  const [profiles, setProfiles] = useState<MCPProcessingProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState<string | null>(null);

  // Load processing profiles on mount
  useEffect(() => {
    loadProcessingProfiles();
  }, []);

  const loadProcessingProfiles = async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/v1/dms/mcp/processing-profiles');
      // const data = await response.json();

      // Mock processing profiles
      const mockProfiles: MCPProcessingProfile[] = [
        {
          id: 'default',
          name: 'Default Processing',
          description: 'Standard AI processing with OCR, classification, and basic analysis',
          aiTasks: {
            ocr: true,
            classification: true,
            citation_extraction: true,
            risk_assessment: true
          },
          estimatedProcessingTime: 60,
          confidenceThreshold: 75
        },
        {
          id: 'contract_risk_review',
          name: 'Contract Risk Review',
          description: 'Specialized analysis for contracts with risk assessment and compliance checking',
          aiTasks: {
            ocr: true,
            classification: true,
            citation_extraction: true,
            risk_assessment: true,
            legal_analysis: true
          },
          estimatedProcessingTime: 120,
          confidenceThreshold: 85
        },
        {
          id: 'discovery_redaction',
          name: 'Discovery Redaction',
          description: 'Automated redaction for discovery documents with privacy protection',
          aiTasks: {
            ocr: true,
            classification: true,
            citation_extraction: false,
            risk_assessment: false,
            redaction: true
          },
          estimatedProcessingTime: 180,
          confidenceThreshold: 90
        },
        {
          id: 'pleading_classification',
          name: 'Pleading Classification',
          description: 'Advanced classification for legal pleadings and court documents',
          aiTasks: {
            ocr: true,
            classification: true,
            citation_extraction: true,
            risk_assessment: false,
            legal_analysis: true
          },
          estimatedProcessingTime: 90,
          confidenceThreshold: 80
        }
      ];

      setProfiles(mockProfiles);
    } catch (err) {
      setError('Failed to load processing profiles');
      console.error('Processing profiles error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSelect = (profileId: string) => {
    onProfileChange(profileId);
  };

  const getProfileIcon = (profileId: string) => {
    switch (profileId) {
      case 'contract_risk_review':
        return '📋';
      case 'discovery_redaction':
        return '🔒';
      case 'pleading_classification':
        return '⚖️';
      default:
        return '📄';
    }
  };

  const getProfileColor = (profileId: string) => {
    switch (profileId) {
      case 'contract_risk_review':
        return 'border-lex-accent bg-lex-accent-soft text-lex-accent';
      case 'discovery_redaction':
        return 'border-red-200 bg-red-50 text-red-700';
      case 'pleading_classification':
        return 'border-purple-200 bg-purple-50 text-purple-700';
      default:
        return 'border-gray-200 bg-gray-50 text-gray-700';
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (disabled) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CogIcon className="h-5 w-5 text-purple-600" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            MCP Processing Profile
          </span>
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
          {error}
        </div>
      )}

      {/* Profiles Grid */}
      {profiles.length > 0 && (
        <div className="grid grid-cols-1 gap-3">
          {profiles.map((profile) => (
            <div
              key={profile.id}
              className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                selectedProfile === profile.id
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
              onClick={() => handleProfileSelect(profile.id)}
            >
              {/* Selection Indicator */}
              {selectedProfile === profile.id && (
                <div className="absolute top-3 right-3">
                  <CheckIcon className="h-5 w-5 text-purple-600" />
                </div>
              )}

              <div className="flex items-start gap-3">
                {/* Profile Icon */}
                <div className="text-2xl">{getProfileIcon(profile.id)}</div>

                {/* Profile Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {profile.name}
                    </h4>
                    <span className={`px-2 py-1 text-xs rounded-full border ${getProfileColor(profile.id)}`}>
                      {profile.id.replace('_', ' ')}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {profile.description}
                  </p>

                  {/* Profile Stats */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <ClockIcon className="h-3 w-3" />
                      <span>{formatTime(profile.estimatedProcessingTime)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <SparklesIcon className="h-3 w-3" />
                      <span>{profile.confidenceThreshold}% confidence</span>
                    </div>
                  </div>

                  {/* AI Tasks */}
                  <div className="mt-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(profile.aiTasks).map(([task, enabled]) => (
                        <span
                          key={task}
                          className={`px-2 py-1 text-xs rounded-full ${
                            enabled
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                              : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                          }`}
                        >
                          {task.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Details Toggle */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetails(showDetails === profile.id ? null : profile.id);
                }}
                className="absolute bottom-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <InformationCircleIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Profile Details */}
      {showDetails && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Profile Details
          </h4>
          <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
            <p><strong>Processing Time:</strong> Varies based on document complexity and size</p>
            <p><strong>Confidence Threshold:</strong> Minimum confidence level for automated processing</p>
            <p><strong>AI Tasks:</strong> Specific AI capabilities enabled for this profile</p>
            <p><strong>Use Case:</strong> Optimized for specific document types and workflows</p>
          </div>
        </div>
      )}

      {/* Info Panel */}
      <div className="bg-lex-accent-soft border border-lex-accent rounded-lg p-3">
        <div className="flex items-start gap-2">
          <InformationCircleIcon className="h-4 w-4 text-lex-accent mt-0.5 flex-shrink-0" />
          <div className="text-xs text-lex-accent">
            <p className="font-medium mb-1">MCP Processing Profiles</p>
            <p>Different LangGraph workflows optimized for specific document types and legal tasks. Each profile uses different AI agents and processing strategies.</p>
          </div>
        </div>
      </div>

      {/* No Profiles Message */}
      {profiles.length === 0 && !loading && (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <CogIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No processing profiles available</p>
          <p className="text-xs">Contact your administrator to configure MCP profiles</p>
        </div>
      )}
    </div>
  );
}; 